/**
 * Everything one step can be set to. Opened from a step's row.
 *
 * Who decides it is the heart of it: a role (anyone holding it, or only
 * those in the document's own department), or people named one by one, which
 * is how "Medical director" means one doctor and "P&T Committee" its members.
 */

import { useState, type FormEvent } from 'react';
import { MultiSelect } from '../../shared/MultiSelect';
import { useUpdateStep, type ApprovalStep } from './approvalsData';
import { ASSIGNABLE_ROLES, ROLE_DISPLAY_NAMES, isRole, type Role } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { STEP_ASSIGNEES_MAX, STEP_DUE_DAYS_MAX, STEP_REQUIRED_APPROVALS_MAX, TITLE_MAX } from '../../types/fieldLimits';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';

const STEP_ROLES: Role[] = ASSIGNABLE_ROLES.filter((r) => r === 'reviewer' || r === 'approver' || r === 'admin');

interface Props {
  step: ApprovalStep;
  /** Whether there is a step above this one to run beside. */
  hasStepAbove: boolean;
  people: readonly { id: string; name: string }[];
  onDone: (message: string | null) => void;
}

export function StepSettings({ step, hasStepAbove, people, onDone }: Props) {
  const update = useUpdateStep();
  const [name, setName] = useState(step.name);
  const [roleId, setRoleId] = useState<Role>(step.roleId && isRole(step.roleId) ? step.roleId : 'approver');
  const [held, setHeld] = useState<'role' | 'people'>((step.assigneeUids ?? []).length > 0 ? 'people' : 'role');
  const [assignees, setAssignees] = useState<string[]>(step.assigneeUids ?? []);
  const [departmentOnly, setDepartmentOnly] = useState(Boolean(step.departmentOnly));
  const [needed, setNeeded] = useState(String(step.requiredApprovals ?? 1));
  const [isCommittee, setIsCommittee] = useState(Boolean(step.isCommittee));
  const [parallel, setParallel] = useState(Boolean(step.parallelWithPrevious));
  const [dueDays, setDueDays] = useState(step.dueDays ? String(step.dueDays) : '');
  const id = (n: string) => `step-${step.stepId}-${n}`;

  const need = Number(needed);
  const due = dueDays.trim() === '' ? null : Number(dueDays);
  const problems: string[] = [];
  if (!name.trim()) problems.push('Give the step a name.');
  if (held === 'people' && assignees.length === 0) problems.push('Choose at least one person, or let a role decide it.');
  if (held === 'people' && assignees.length > STEP_ASSIGNEES_MAX) problems.push(`Name at most ${STEP_ASSIGNEES_MAX} people.`);
  if (!Number.isInteger(need) || need < 1 || need > STEP_REQUIRED_APPROVALS_MAX) problems.push(`How many must approve is a whole number from 1 to ${STEP_REQUIRED_APPROVALS_MAX}.`);
  else if (held === 'people' && need > assignees.length && assignees.length > 0) problems.push(`Only ${assignees.length} ${assignees.length === 1 ? 'person is' : 'people are'} named, so ${need} cannot be needed.`);
  if (due !== null && (!Number.isInteger(due) || due < 1 || due > STEP_DUE_DAYS_MAX)) problems.push(`Days allowed is a whole number from 1 to ${STEP_DUE_DAYS_MAX}, or blank for no deadline.`);

  function save(e: FormEvent) {
    e.preventDefault();
    if (problems.length > 0) return;
    update.mutate({
      stepId: step.stepId, name: name.trim(), roleId,
      assigneeUids: held === 'people' ? assignees : [],
      departmentOnly: held === 'role' ? departmentOnly : false,
      requiredApprovals: need, isCommittee, parallelWithPrevious: hasStepAbove ? parallel : false, dueDays: due,
    }, { onSuccess: () => onDone(`Saved "${name.trim()}". Approvals already under way keep the step as it was.`) });
  }

  return (
    <form className={own.stepEdit} onSubmit={save} aria-label={`Settings for the step ${step.name}`}>
      <div className={own.textRow}>
        <div>
          <label className={own.label} htmlFor={id('name')}>What this step is called</label>
          <input id={id('name')} maxLength={TITLE_MAX} className={own.input} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className={own.label} htmlFor={id('needed')}>How many must approve</label>
          <input id={id('needed')} type="number" min={1} max={STEP_REQUIRED_APPROVALS_MAX} className={own.input} style={{ maxWidth: 120 }} value={needed} onChange={(e) => setNeeded(e.target.value)} aria-describedby={id('needed-hint')} />
          <p className={own.itemMeta} id={id('needed-hint')}>1 means the first person to approve finishes the step. More than 1 means that many different people must.</p>
        </div>
      </div>

      {/* On a committee's step the decision is the committee's; what is chosen
          here is only who may write it down. Saying "who decides it" over these
          controls would say the opposite of the record they produce. */}
      <fieldset className={own.fieldset}>
        <legend className={own.legend}>{isCommittee ? 'Who records the committee\u2019s decision' : 'Who decides it'}</legend>
        {isCommittee && (
          <p className={own.itemMeta}>
            The committee decides. Whoever is chosen here signs in and records what it resolved, with the date it
            met. Committee members need no account of their own.
          </p>
        )}
        <label className={own.choice}><input type="radio" name={id('held')} checked={held === 'role'} onChange={() => setHeld('role')} /><span>Anyone holding a role</span></label>
        <label className={own.choice}><input type="radio" name={id('held')} checked={held === 'people'} onChange={() => setHeld('people')} /><span>People I name</span></label>
      </fieldset>

      {held === 'role' ? (
        <div className={own.textRow}>
          <div>
            <label className={own.label} htmlFor={id('role')}>Role</label>
            <select id={id('role')} className={own.select} value={roleId} onChange={(e) => setRoleId(e.target.value as Role)}>
              {STEP_ROLES.map((r) => <option key={r} value={r}>{ROLE_DISPLAY_NAMES[r]}</option>)}
            </select>
          </div>
          <div>
            <label className={own.choice} style={{ marginTop: 'var(--sp-5)' }}>
              <input type="checkbox" id={id('dept')} checked={departmentOnly} onChange={(e) => setDepartmentOnly(e.target.checked)} />
              <span>Only people in the document&rsquo;s own department</span>
            </label>
            <p className={own.itemMeta}>So a department director sees their own department&rsquo;s documents, not everyone&rsquo;s. The department is the first one the document is filed under, and people belong to departments on the Staff page.</p>
          </div>
        </div>
      ) : (
        <div>
          <div className={own.label} aria-hidden="true">People</div>
          <MultiSelect id={id('people')} label={isCommittee ? "People who record this committee's decision" : 'People who decide this step'} options={people} value={assignees} onChange={setAssignees} placeholder="Choose people" />
          <p className={own.itemMeta}>
            Only these people are {isCommittee ? 'asked to record it' : 'asked'}, whatever role they hold. An administrator can still
            {isCommittee ? ' record it' : ' decide it'}, so the document is not stranded if someone leaves.
          </p>
        </div>
      )}

      <div className={own.checks}>
        <label className={own.choice}>
          <input type="checkbox" id={id('committee')} checked={isCommittee} onChange={(e) => setIsCommittee(e.target.checked)} />
          <span>This is a committee&rsquo;s step</span>
        </label>
        <p className={own.itemMeta}>The decision belongs to the committee. Whoever records it gives the date the committee met, and can add the vote and where the minutes are kept.</p>
        {hasStepAbove && (
          <>
            <label className={own.choice}>
              <input type="checkbox" id={id('parallel')} checked={parallel} onChange={(e) => setParallel(e.target.checked)} />
              <span>At the same time as the step above</span>
            </label>
            <p className={own.itemMeta}>Both are asked at once, and the document moves on when both are done.</p>
          </>
        )}
      </div>

      <div>
        <label className={own.label} htmlFor={id('due')}>Days allowed <span className={own.optional}>(blank for no deadline)</span></label>
        <input id={id('due')} type="number" min={1} max={STEP_DUE_DAYS_MAX} className={own.input} style={{ maxWidth: 120 }} value={dueDays} onChange={(e) => setDueDays(e.target.value)} />
        <p className={own.itemMeta}>When it passes, the people asked and the administrators are told once. Nothing is approved automatically.</p>
      </div>

      {problems.length > 0 && <ul className={own.error} aria-label="What needs fixing">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
      {update.error && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{isCallFailure(update.error) || update.error instanceof Error ? update.error.message : 'That could not be saved.'}</div>}

      <div className={styles.actions}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={update.isPending || problems.length > 0}>{update.isPending ? 'Saving…' : 'Save step'}</button>
        <button type="button" className={styles.btn} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}
